import { Card } from "@mui/material";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import CampaignMessage from "@/components/molecules/ChatBalloon/components/CampaignMessage";
import ChatRemoteMedia from "@/components/molecules/ChatBalloon/components/ChatRemoteMedia";
import InteractiveOptions from "@/components/molecules/ChatBalloon/components/InteractiveOptions";
import MessageDeliveryStatusIcon from "@/components/molecules/ChatBalloon/components/MessageDeliveryStatusIcon";
import WhatsAppMessageText from "@/components/molecules/ChatBalloon/components/WhatsAppMessageText";
import {
  buildCampaignRender,
  isCampaignPlaceholder,
} from "@/helpers/chat-campaign-template";
import {
  parseFormattedText,
  toInteractiveOptions,
} from "@/helpers/chat-formatted-text";
import { formatMessageTime } from "@/helpers/date-helpers";
import { isSticker, unicodeToEmoji } from "@/helpers/messages-helpers";
import { isValidUrl } from "@/helpers/url-validation";
import useChatBalloonData from "@/hooks/use-chat-balloon-data";
import { IMessage } from "@/interfaces/message.interface";
import { CurrentChatMessages } from "@/messages/chat/current-chat.messages";

import DisplayMedia from "./components/DisplayMedia";
import { CHAT_MEDIA_MAX_WIDTH_PX } from "./media-size";

type ChatBalloonProps = {
  chatMessage: IMessage;
  messages: CurrentChatMessages;
};

export default function ChatBalloon({ chatMessage, messages }: ChatBalloonProps) {
  const mediaId = chatMessage.midiaId ? Number(chatMessage.midiaId) : 0;
  const { media, mediaURL, isLoading } = useChatBalloonData(mediaId);
  const isSentByUser = chatMessage.customerExternalId === chatMessage.fromId;
  const { midiaId, attachment, attachmentCaption, text, rowDate, type } = chatMessage;
  const textColor = isSentByUser ? "white" : "dark";

  const formatted = parseFormattedText(chatMessage.formattedText);
  const interactiveOptions = formatted ? toInteractiveOptions(formatted) : [];
  const shouldTryCampaign =
    Boolean(chatMessage.campaignId) || isCampaignPlaceholder(text);
  const campaign = shouldTryCampaign
    ? buildCampaignRender({
        templateText: chatMessage.templateText,
        placeholderText: text,
        campaignImageUrl: chatMessage.campaignImageUrl,
        campaignText: chatMessage.campaignText,
        campaignCode: chatMessage.campaignCode,
        campaignDiscountPercentage: chatMessage.campaignDiscountPercentage,
      })
    : null;

  const hasFormattedContent = Boolean(formatted?.message?.trim() || interactiveOptions.length);
  const hasCampaignContent = Boolean(
    campaign?.imageUrl || campaign?.body?.trim() || campaign?.footerCode
  );
  const hasMedia =
    Boolean(midiaId) ||
    Boolean(attachment && isValidUrl(attachment)) ||
    Boolean(campaign?.imageUrl);
  const hasText = Boolean(text?.trim()) && !hasFormattedContent && !hasCampaignContent;
  const hasVisibleContent =
    hasFormattedContent || hasCampaignContent || hasMedia || Boolean(attachmentCaption) || hasText;

  const renderMessageContent = () => {
    if (hasFormattedContent && formatted) {
      return (
        <>
          {formatted.message && (
            <WhatsAppMessageText text={formatted.message} color={textColor} />
          )}
          <InteractiveOptions
            options={interactiveOptions}
            isSentByUser={isSentByUser}
            href={
              formatted.type === "CtaUrl" && formatted.url && isValidUrl(formatted.url)
                ? formatted.url
                : undefined
            }
          />
        </>
      );
    }

    if (hasCampaignContent && campaign) {
      return <CampaignMessage campaign={campaign} isSentByUser={isSentByUser} />;
    }

    if (isSticker(text)) {
      return <MDTypography variant="button">{unicodeToEmoji(text)}</MDTypography>;
    }

    if (!text) return null;

    return <WhatsAppMessageText text={text} color={textColor} />;
  };

  const showStoredMedia = Boolean(midiaId);
  const showAttachmentMedia =
    !showStoredMedia && !hasCampaignContent && Boolean(attachment) && isValidUrl(attachment || "");

  if (!hasVisibleContent) {
    return null;
  }

  return (
    <Card
      sx={(theme) => ({
        background: isSentByUser ? theme.palette.primary.main : theme.palette.white.main,
        color: isSentByUser ? theme.palette.white.main : theme.palette.text.main,
        width: "fit-content",
        alignSelf: isSentByUser ? "flex-end" : "flex-start",
        flexShrink: 0,
        whiteSpace: "pre-wrap",
        maxWidth: hasMedia
          ? `min(90%, ${theme.functions.pxToRem(CHAT_MEDIA_MAX_WIDTH_PX)})`
          : "90%",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        overflow: "hidden",
        position: "relative",
        marginBottom: theme.functions.pxToRem(8),
      })}
    >
      <MDBox p={2}>
        {showStoredMedia && (
          <DisplayMedia
            media={media}
            messages={messages}
            isLoading={isLoading}
            mediaURL={mediaURL}
            isSentByUser={isSentByUser}
          />
        )}
        {showAttachmentMedia && attachment && (
          <ChatRemoteMedia
            url={attachment}
            type={type}
            alt={attachmentCaption || ""}
            messages={messages}
          />
        )}
        {attachmentCaption && !campaign && (
          <MDTypography variant="button" sx={{ whiteSpace: "pre-wrap", display: "block" }}>
            {attachmentCaption}
          </MDTypography>
        )}
        {renderMessageContent()}
        <MDBox
          mt={1}
          display="flex"
          alignItems="center"
          justifyContent={isSentByUser ? "flex-end" : "flex-start"}
        >
          <MDTypography variant="caption" color={isSentByUser ? "white" : "text"}>
            {formatMessageTime(rowDate)}
          </MDTypography>
          {isSentByUser && (
            <MessageDeliveryStatusIcon chatMessage={chatMessage} messages={messages} />
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
}
